/**
 * Componente CorporateTable - Tabla Corporativa Reutilizable
 * Incluye header con gradiente, zebra striping y estilos mejorados
 * ESTÁNDAR: Usar en toda la aplicación para tablas de datos
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableProps,
  TableContainerProps,
  TableSortLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export type SortDirection = 'asc' | 'desc' | false;

export interface SortableHeader {
  /**
   * Texto del header
   */
  label: string;
  
  /**
   * Clave única para identificar la columna
   */
  key: string;
  
  /**
   * Si es true, la columna es ordenable
   */
  sortable?: boolean;
  
  /**
   * Ancho de la columna (ej: "250px", "20%")
   */
  width?: string | number;
}

interface CorporateTableProps {
  /**
   * Filas de la tabla (TableRow components)
   */
  children: React.ReactNode;
  
  /**
   * Headers de la tabla (TableCell components o SortableHeader para ordenamiento)
   */
  headers: (React.ReactNode | SortableHeader)[];
  
  /**
   * Si es true, aplica zebra striping (alternar colores de filas)
   */
  zebraStriping?: boolean;
  
  /**
   * Si es true, aplica header con gradiente
   */
  gradientHeader?: boolean;
  
  /**
   * Si es true, aplica hover effect en las filas
   */
  hoverRows?: boolean;
  
  /**
   * Props adicionales para TableContainer
   */
  containerProps?: TableContainerProps;
  
  /**
   * Props adicionales para Table
   */
  tableProps?: TableProps;
  
  /**
   * Función callback cuando se hace clic en ordenar
   */
  onSort?: (key: string, direction: SortDirection) => void;
  
  /**
   * Columna actualmente ordenada
   */
  sortBy?: string;
  
  /**
   * Dirección del ordenamiento actual
   */
  sortDirection?: SortDirection;
}

interface StyledTableRowProps {
  gradientHeader: boolean;
}

interface StyledTableBodyProps {
  zebraStriping: boolean;
  hoverRows: boolean;
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  overflowX: 'auto',
  overflowY: 'hidden',
}));

const StyledHeaderRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'gradientHeader',
})<StyledTableRowProps>(({ theme, gradientHeader }) => {
  const customTheme = theme as any;
  return {
    background: gradientHeader
      ? customTheme.custom.gradients.primary
      : theme.palette.background.paper,
  };
});

const StyledHeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'gradientHeader',
})<StyledTableRowProps>(({ theme, gradientHeader }) => ({
  color: gradientHeader ? theme.palette.common.white : theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '0.7rem',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: gradientHeader ? 'none' : `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  userSelect: 'none',
}));

const StyledSortLabel = styled(TableSortLabel)(({ theme }) => ({
  color: 'inherit !important',
  '& .MuiTableSortLabel-icon': {
    color: 'inherit !important',
    opacity: 0.7,
  },
  '&:hover .MuiTableSortLabel-icon': {
    opacity: 1,
  },
  '&.Mui-active .MuiTableSortLabel-icon': {
    opacity: 1,
  },
}));

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'zebraStriping' && prop !== 'hoverRows',
})<StyledTableBodyProps>(({ theme, zebraStriping, hoverRows }) => ({
  '& tr': {
    backgroundColor: theme.palette.background.paper,
    transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  },
  ...(zebraStriping && {
    '& tr:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  }),
  ...(hoverRows && {
    '& tr:hover': {
      backgroundColor: theme.palette.action.selected,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
    },
  }),
}));

export const CorporateTable: React.FC<CorporateTableProps> = ({
  children,
  headers,
  zebraStriping = true,
  gradientHeader = true,
  hoverRows = true,
  containerProps,
  tableProps,
  onSort,
  sortBy,
  sortDirection,
}) => {
  const handleSort = (header: SortableHeader) => {
    if (!onSort || !header.sortable) return;
    
    if (sortBy === header.key) {
      // Si ya está ordenado por esta columna, cambiar dirección
      if (sortDirection === 'asc') {
        onSort(header.key, 'desc');
      } else if (sortDirection === 'desc') {
        onSort(header.key, false);
      } else {
        onSort(header.key, 'asc');
      }
    } else {
      // Nueva columna, empezar con asc
      onSort(header.key, 'asc');
    }
  };

  const isSortableHeader = (header: React.ReactNode | SortableHeader): header is SortableHeader => {
    return typeof header === 'object' && header !== null && 'key' in header;
  };

  return (
    <StyledTableContainer
      component={Paper}
      {...containerProps}
    >
      <Table {...tableProps}>
        <TableHead>
          <StyledHeaderRow gradientHeader={gradientHeader}>
            {headers.map((header, index) => {
              const isSortable = isSortableHeader(header) && header.sortable;
              const isActive = isSortableHeader(header) && sortBy === header.key;
              
              const headerWidth = isSortableHeader(header) ? header.width : undefined;
              
              return (
                <StyledHeaderCell
                  key={index}
                  component="th"
                  scope="row"
                  gradientHeader={gradientHeader}
                  onClick={isSortable ? () => handleSort(header) : undefined}
                  sx={isSortable ? { cursor: 'pointer' } : {}}
                  width={headerWidth}
                >
                  {isSortableHeader(header) ? (
                    <StyledSortLabel
                      active={isActive}
                      direction={isActive && sortDirection ? sortDirection : 'asc'}
                      hideSortIcon={!isActive}
                    >
                      {header.label}
                    </StyledSortLabel>
                  ) : (
                    header
                  )}
                </StyledHeaderCell>
              );
            })}
          </StyledHeaderRow>
        </TableHead>
        <StyledTableBody zebraStriping={zebraStriping} hoverRows={hoverRows}>
          {children}
        </StyledTableBody>
      </Table>
    </StyledTableContainer>
  );
};

